import React from 'react';
import JobDetails from './JobDetails';
import { parseDescriptionList } from '../../utils/parsers';

/**
 * Job card component to display a single job listing
 * @param {Object} props - Component props
 * @param {Object} props.job - Job data object
 * @param {boolean} props.isSaved - Whether the job is saved
 * @param {Function} props.onSave - Function to call when saving a job
 * @param {Function} props.onRemove - Function to call when removing a job
 * @param {boolean} props.showSaveButton - Whether to show the save button
 * @param {boolean} props.isSaving - Whether the job is currently being saved or removed
 * @returns {JSX.Element} Job card component
 */
const JobCard = ({ 
  job, 
  isSaved, 
  onSave, 
  onRemove, 
  showSaveButton = true,
  isSaving = false
}) => {
  const jobCardStyle = {
    border: '1px solid #333', // Darker border
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '15px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)', // Darker shadow
    backgroundColor: '#1e1e1e', // Dark gray background
    color: '#ffffff', // White text
  };
  
  // Apply Now button style - green on main page, blue on saved jobs page
  const applyButtonStyle = {
    padding: '10px 15px',
    backgroundColor: showSaveButton ? '#28a745' : '#2980B9', // Green on main page, blue on saved page
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginRight: '10px',
    fontSize: '14px',
  };
  
  // Save job button style - blue when not saved, gray when saved
  const saveButtonStyle = {
    padding: '10px 15px',
    backgroundColor: isSaved ? '#6c757d' : '#007bff', // Gray when saved, blue when not
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginRight: '10px',
    fontSize: '14px',
  };
  
  // Remove button style - always red
  const removeButtonStyle = {
    padding: '10px 15px',
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginRight: '10px',
    fontSize: '14px',
  };
  
  return (
    <div style={jobCardStyle}>
      <h3 style={{ marginBottom: '10px' }}>
        <span style={{ color: "#2980B9" }}>{job.title}</span>
        {" | "}
        <span style={{ color: "#CE93D8" }}>{job.organization}</span>
      </h3>
      
      {/* Job description bullets */}
      {job.descript_short && (
        <div style={{ margin: "10px 0" }}>
          <ul style={{ margin: "5px 0 0 20px", color: "#ffffff" }}>
            {parseDescriptionList(job.descript_short).map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Job details */}
      <JobDetails job={job} />
      
      {/* Action buttons */}
      <div style={{ display: 'flex', marginTop: '10px' }}>
        <button
          onClick={() => window.open(job.original_link, "_blank")}
          style={applyButtonStyle}
        >
          Apply Now
        </button>
        
        {showSaveButton && (
          <button 
            onClick={(e) => {
              // Prevent event bubbling to avoid any parent handlers
              e.stopPropagation();
              
              // Prevent action if already saving/removing another job
              if (isSaving) return;
              
              // Only pass cis_fuss_id
              if (isSaved) {
                onRemove(job.cis_fuss_id);
              } else if (job.cis_fuss_id) {
                // Only save if job has a valid cis_fuss_id
                onSave(job.cis_fuss_id);
              } else {
                console.error('Cannot save job - no cis_fuss_id found:', job);
                alert('Cannot save this job - ID missing. Please try another job.');
              }
            }}
            style={{
              ...saveButtonStyle,
              opacity: isSaving ? 0.7 : 1,
              cursor: isSaving ? 'default' : 'pointer'
            }}
            disabled={isSaving}
          >
            {isSaving
              ? "Processing..." 
              : (isSaved ? "Unsave Job" : "Save Job")
            }
          </button>
        )}
        
        {!showSaveButton && isSaved && (
          <button 
            onClick={(e) => {
              // Prevent event bubbling to avoid any parent handlers
              e.stopPropagation();
              
              // Prevent action if already saving/removing a job
              if (isSaving) return;
              
              // Call remove function with cis_fuss_id
              onRemove(job.cis_fuss_id);
            }}
            style={{
              ...removeButtonStyle,
              opacity: isSaving ? 0.7 : 1,
              cursor: isSaving ? 'default' : 'pointer'
            }}
            disabled={isSaving}
          >
            {isSaving ? "Processing..." : "Remove"}
          </button>
        )}
      </div>
    </div>
  );
};

export default JobCard;